import { memo, useMemo, useRef, useState } from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'
import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'

import { SUB_TIMER_LIMITS } from '~shared/rundown/cue'
import {
  getSubTimerCueRun,
  isSameSubTimerCueRun,
} from '~shared/rundown/sub-timer-run'

import { runnerAtom, timeOffsetAtom } from '@/store/runner.store'

import { useCueSnapshot } from '@/features/rundown/data/cues.store'

import { createSubTimer } from './sub-timers'
import type { SubTimer } from './sub-timers'
import { saveSubTimers } from './sub-timers.api'
import { SubTimerSortable } from './sub-timers.dnd'
import { SUB_TIMER_POPOVER_CLASS, SubTimerForm } from './SubTimerForm'
import type { SubTimerFormHandle } from './SubTimerForm'
import { SubTimerItem } from './SubTimerItem'

type SubTimerListProps = {
  rundownId: string
  cueId: string
  /** Add, edit, delete and reorder: EDIT access on a cue without Prevent edits. */
  canEdit: boolean
  /** Start, pause and reset: SHOWCALL access, like the transport controls. */
  canRun: boolean
}

const runnerIdAtom = selectAtom(runnerAtom, (runner) => runner?.id ?? null)

/**
 * A cue's sub timers, under its duration.
 *
 * Memoised on primitive props so the parent's per-tick timing renders stop
 * here. Subscribes to this cue's settings and its own slice of the runner.
 */
export const SubTimerList = memo(function SubTimerList({
  rundownId,
  cueId,
  canEdit,
  canRun,
}: SubTimerListProps) {
  // Reads only `subTimers`, so other edits to the cue re-render only when the
  // snapshot replaces this array too
  const timers = useCueSnapshot(cueId)?.subTimers
  const timeOffset = useAtomValue(timeOffsetAtom)
  const runnerId = useAtomValue(runnerIdAtom)
  // Compared by value, so runner writes that leave this cue alone (other cues,
  // other fields) do not re-render the list
  const cueRun = useAtomValue(
    useMemo(
      () =>
        selectAtom(
          runnerAtom,
          (runner) => getSubTimerCueRun(runner, cueId),
          isSameSubTimerCueRun,
        ),
      [cueId],
    ),
  )
  const [addOpen, setAddOpen] = useState(false)
  const addFormRef = useRef<SubTimerFormHandle>(null)

  // A dropped order shows at once. It applies only to the list it was made
  // from, so the saved list replaces it as soon as the snapshot arrives.
  const [pendingOrder, setPendingOrder] = useState<{
    from: readonly SubTimer[]
    ids: string[]
  } | null>(null)
  const ordered =
    timers && pendingOrder?.from === timers
      ? pendingOrder.ids
          .map((id) => timers.find((t) => t.id === id))
          .filter((t) => t !== undefined)
      : timers
  const sortable = canEdit && (ordered?.length ?? 0) > 1
  const byId = new Map(ordered?.map((t) => [t.id, t]))

  const reorder = (ids: string[]) => {
    if (!timers) return
    setPendingOrder({ from: timers, ids })
    saveSubTimers(rundownId, cueId, (list) => [
      ...ids
        .map((id) => list.find((t) => t.id === id))
        .filter((t) => t !== undefined),
      // Anything added meanwhile keeps its place at the end
      ...list.filter((t) => !ids.includes(t.id)),
    ]).catch(() => setPendingOrder(null)) // toasted; show the saved order again
  }

  const renderItem = (id: string, overlay = false) => {
    const timer = byId.get(id)
    if (!timer) return null
    return (
      <SubTimerItem
        key={id}
        rundownId={rundownId}
        runnerId={runnerId}
        cueId={cueId}
        settings={timer}
        cueRun={cueRun}
        timeOffset={timeOffset}
        canEdit={canEdit}
        canRun={canRun}
        handle={
          !canEdit
            ? undefined
            : sortable
              ? overlay
                ? 'static'
                : 'drag'
              : 'disabled'
        }
      />
    )
  }

  const addClass = cn(
    'h-5 rounded-sm text-[11px] text-white/60 hover:bg-black/30 hover:text-white outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-white/60',
    'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
    addOpen && 'opacity-100',
  )
  const addContent = (
    <>
      <FontAwesomeIcon
        icon={faPlus}
        className="mr-1"
      />
      Sub timer
    </>
  )

  return (
    <div className="flex flex-col gap-1 pt-1">
      {ordered &&
        (sortable ? (
          <SubTimerSortable
            ids={ordered.map((t) => t.id)}
            onReorder={reorder}
            renderItem={renderItem}
          />
        ) : (
          ordered.map((t) => renderItem(t.id))
        ))}

      {/* Popover mounts only while open: hundreds of idle rows skip its hooks */}
      {canEdit &&
        (timers?.length ?? 0) < SUB_TIMER_LIMITS.PER_CUE &&
        (addOpen ? (
          <Popover
            open
            onOpenChange={(open, { reason }) => {
              // Closing adds the timer if anything was filled in; Escape discards
              if (!open && reason !== 'escape-key') addFormRef.current?.commit()
              setAddOpen(open)
            }}
          >
            <PopoverTrigger className={addClass}>{addContent}</PopoverTrigger>
            <PopoverContent className={SUB_TIMER_POPOVER_CLASS}>
              <SubTimerForm
                ref={addFormRef}
                title="New Sub Timer"
                submitLabel="Add"
                onCancel={() => setAddOpen(false)}
                onSubmit={(input) => {
                  void saveSubTimers(rundownId, cueId, (list) => [
                    ...list,
                    createSubTimer(input),
                  ])
                  setAddOpen(false)
                }}
              />
            </PopoverContent>
          </Popover>
        ) : (
          <button
            type="button"
            className={addClass}
            onClick={() => setAddOpen(true)}
          >
            {addContent}
          </button>
        ))}
    </div>
  )
})
